import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Session timeout constants (in milliseconds)
const SESSION_CONFIG = {
  // Standard users: 14 days inactivity, 30 days absolute
  user: {
    inactivityTimeout: 14 * 24 * 60 * 60 * 1000, // 14 days
    absoluteExpiration: 30 * 24 * 60 * 60 * 1000, // 30 days
  },
  // Admins/Managers: 7 days inactivity, 30 days absolute
  admin: {
    inactivityTimeout: 7 * 24 * 60 * 60 * 1000, // 7 days
    absoluteExpiration: 30 * 24 * 60 * 60 * 1000, // 30 days
  },
};

const STORAGE_KEYS = {
  lastActivity: 'returnify_last_activity',
  sessionStart: 'returnify_session_start',
  userRole: 'returnify_user_role', // Cache role to avoid extra API calls
};

type UserRole = 'user' | 'admin' | 'manager' | 'support';

interface SessionManagerOptions {
  onSessionExpired?: () => void;
}

/**
 * Hook to manage session persistence with activity-based timeouts.
 * Implements different timeout rules for standard users vs admins/managers.
 */
export const useSessionManager = (options?: SessionManagerOptions) => {
  const navigate = useNavigate();
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get session config based on role
  const getSessionConfig = useCallback((role: UserRole) => {
    if (role === 'admin' || role === 'manager') {
      return SESSION_CONFIG.admin;
    }
    return SESSION_CONFIG.user;
  }, []);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.lastActivity, Date.now().toString());
  }, []);

  // Initialize session tracking
  const initializeSession = useCallback((role: UserRole = 'user') => {
    const now = Date.now().toString();
    
    // Only set session start if not already set
    if (!localStorage.getItem(STORAGE_KEYS.sessionStart)) {
      localStorage.setItem(STORAGE_KEYS.sessionStart, now);
    }
    
    localStorage.setItem(STORAGE_KEYS.lastActivity, now);
    localStorage.setItem(STORAGE_KEYS.userRole, role);
  }, []);

  // Clear session data
  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.lastActivity);
    localStorage.removeItem(STORAGE_KEYS.sessionStart);
    localStorage.removeItem(STORAGE_KEYS.userRole);
    
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
  }, []);

  // Force logout
  const forceLogout = useCallback(async (reason: string) => {
    clearSession();
    
    try {
      await supabase.auth.signOut();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error signing out:', error);
      }
    }
    
    toast.info('Session expired', {
      description: reason,
    });
    
    options?.onSessionExpired?.();
    navigate('/auth');
  }, [clearSession, navigate, options]);

  // Check if session is still valid
  const checkSessionValidity = useCallback(() => {
    const lastActivity = localStorage.getItem(STORAGE_KEYS.lastActivity);
    const sessionStart = localStorage.getItem(STORAGE_KEYS.sessionStart);
    const role = (localStorage.getItem(STORAGE_KEYS.userRole) as UserRole) || 'user';
    
    if (!lastActivity || !sessionStart) {
      return true; // No session data, let auth handle it
    }
    
    const config = getSessionConfig(role);
    const now = Date.now();
    const lastActivityTime = parseInt(lastActivity, 10);
    const sessionStartTime = parseInt(sessionStart, 10);
    
    // Check absolute expiration
    if (now - sessionStartTime > config.absoluteExpiration) {
      forceLogout('Your session has expired. Please sign in again.');
      return false;
    }
    
    // Check inactivity timeout
    if (now - lastActivityTime > config.inactivityTimeout) {
      forceLogout('You were signed out due to inactivity.');
      return false;
    }
    
    return true;
  }, [getSessionConfig, forceLogout]);

  // Set up activity listeners
  useEffect(() => {
    const handleActivity = () => {
      updateActivity();
    };

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check session validity periodically (every 5 minutes)
    checkIntervalRef.current = setInterval(() => {
      checkSessionValidity();
    }, 5 * 60 * 1000);

    // Initial check
    checkSessionValidity();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [updateActivity, checkSessionValidity]);

  return {
    initializeSession,
    updateActivity,
    clearSession,
    checkSessionValidity,
  };
};

/**
 * Get the appropriate redirect path based on user role.
 * Called after successful authentication.
 */
export const getRoleBasedRedirect = async (): Promise<string> => {
  try {
    const { data, error } = await supabase.functions.invoke('verify-admin');
    
    if (!error && data?.isAdmin) {
      // Store role for session management
      localStorage.setItem(STORAGE_KEYS.userRole, 'admin');
      return '/admin/tasks';
    }
    
    // Check if manager
    const { data: userData } = await supabase.auth.getUser();
    if (userData?.user) {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userData.user.id)
        .single();
      
      if (roles?.role === 'manager') {
        localStorage.setItem(STORAGE_KEYS.userRole, 'manager');
        return '/admin/tasks';
      }
    }
    
    localStorage.setItem(STORAGE_KEYS.userRole, 'user');
    return '/dashboard';
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error determining redirect:', error);
    }
    return '/dashboard';
  }
};
